import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, vModelText as _vModelText, withDirectives as _withDirectives, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "task-status-transition-modal" }
const _hoisted_2 = { class: "task-status-transition-modal__timeline" }
const _hoisted_3 = { class: "timeline-header" }
const _hoisted_4 = { class: "timeline-section-title" }
const _hoisted_5 = { class: "timeline-current-status" }
const _hoisted_6 = { class: "current-status-label" }
const _hoisted_7 = {
  key: 0,
  class: "timeline-loading"
}
const _hoisted_8 = {
  key: 1,
  class: "integrated-timeline"
}
const _hoisted_9 = { class: "timeline-step-icon" }
const _hoisted_10 = { class: "timeline-step-content" }
const _hoisted_11 = { class: "timeline-step-label" }
const _hoisted_12 = {
  key: 0,
  class: "timeline-step-date"
}
const _hoisted_13 = {
  key: 0,
  class: "task-status-transition-modal__options"
}
const _hoisted_14 = { class: "task-status-transition-modal__label" }
const _hoisted_15 = { class: "status-options-grid" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  viewBox: "0 0 24 24",
  width: "24",
  height: "24"
}
const _hoisted_18 = {
  key: 0,
  fill: "currentColor",
  cx: "12",
  cy: "12",
  r: "8"
}
const _hoisted_19 = {
  key: 1,
  fill: "currentColor",
  d: "M8,5.14V19.14L19,12.14L8,5.14Z"
}
const _hoisted_20 = {
  key: 2,
  fill: "currentColor",
  d: "M14,19H18V5H14M6,19H10V5H6V19Z"
}
const _hoisted_21 = {
  key: 3,
  fill: "currentColor",
  d: "M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"
}
const _hoisted_22 = {
  key: 4,
  fill: "currentColor",
  d: "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
}
const _hoisted_23 = {
  key: 5,
  fill: "currentColor",
  d: "M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
}
const _hoisted_24 = { class: "status-option__content" }
const _hoisted_25 = { class: "status-option__title" }
const _hoisted_26 = {
  key: 0,
  class: "status-option__check"
}
const _hoisted_27 = {
  key: 1,
  class: "task-status-transition-modal__comment"
}
const _hoisted_28 = {
  class: "task-status-transition-modal__label",
  for: "status-comment"
}
const _hoisted_29 = ["placeholder"]
const _hoisted_30 = { class: "task-status-transition-modal__actions" }

import { ref, watch, computed, h, type VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import AppModal from '@/components/base/Modal.vue'
import AppButton from '@/components/base/AppButton.vue'
import projectTasksService from '@/services/projectTasks.service'
import taskStateMachineService from '@/services/taskStateMachine.service'
import { useNotifications } from '@/composables/useNotifications'
import type { TaskStatus, TaskStatusHistoryDto } from '@/types/task'

// Props
interface Props {
  isVisible: boolean
  projectId: number
  taskId: number
  currentStatus: TaskStatus
}

interface Emits {
  confirm: [status: string, comment?: string]
  cancel: []
  close: []
  success: []
}

interface TimelineStep {
  status: string
  date: string | null
  isCurrent: boolean
  isCompleted: boolean
}

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'TaskStatusTransitionModal',
  props: {
    isVisible: { type: Boolean },
    projectId: {},
    taskId: {},
    currentStatus: {}
  },
  emits: ["confirm", "cancel", "close", "success"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props

// Emits
const emit = __emit

// Composables
const { t } = useI18n()
const notifications = useNotifications()

// Timeline interface
const selectedStatus = ref<string | null>(null)
const comment = ref<string>('')
const possibleStatuses = ref<string[]>([])
const isLoading = ref(false)
const loadingTimeline = ref(false)
const statusTimeline = ref<TaskStatusHistoryDto[]>([])

// Computed timeline steps
const timelineSteps = computed<TimelineStep[]>(() => {
  if (!statusTimeline.value || statusTimeline.value.length === 0) {
    // If no timeline, show current status only
    return [{
      status: props.currentStatus,
      date: null,
      isCurrent: true,
      isCompleted: false
    }]
  }
  
  const steps: TimelineStep[] = []
  
  // Add the initial status (fromStatus of the first transition)
  if (statusTimeline.value[0].fromStatus) {
    steps.push({
      status: statusTimeline.value[0].fromStatus,
      date: null, // Initial status doesn't have a date
      isCurrent: false,
      isCompleted: true
    })
  }
  
  // Add all transitions
  statusTimeline.value.forEach((entry: TaskStatusHistoryDto, index: number) => {
    const isLast = index === statusTimeline.value.length - 1
    steps.push({
      status: entry.toStatus,
      date: entry.formattedChangedAt || null,
      isCurrent: isLast && entry.toStatus === props.currentStatus,
      isCompleted: !isLast || entry.toStatus !== props.currentStatus
    })
  })
  
  return steps
})

// Get context-aware action label
const getActionLabel = (status: string): string => {
  // Context-aware action labels for specific transitions
  if (status === 'IN_PROGRESS') {
    if (props.currentStatus === 'PENDING') {
      return t('taskWorkspace.statusActions.START')
    } else if (props.currentStatus === 'ON_HOLD') {
      return t('taskWorkspace.statusActions.RESUME')
    }
  }
  
  return t(`taskWorkspace.statusActions.${status}`)
}

// Methods
const loadPossibleStatuses = () => {
  try {
    const statuses = taskStateMachineService.getPossibleStatuses(props.currentStatus)
    possibleStatuses.value = statuses || []
  } catch (error) {
    console.error('Failed to load possible statuses:', error)
    notifications.notifyError(t('taskWorkspace.errors.loadFailed'))
    possibleStatuses.value = []
  }
}

const loadStatusTimeline = async () => {
  loadingTimeline.value = true
  
  try {
    const timeline = await projectTasksService.getTaskStatusTimeline(
      props.projectId.toString(), 
      props.taskId.toString()
    )
    statusTimeline.value = timeline || []
  } catch (error) {
    console.error('Failed to load task status timeline:', error)
    statusTimeline.value = []
  } finally {
    loadingTimeline.value = false
  }
}

const handleConfirm = async () => {
  if (!selectedStatus.value) return

  isLoading.value = true
  try {
    // Change task status using the new endpoint
    await projectTasksService.changeTaskStatus(
      props.projectId.toString(),
      props.taskId.toString(),
      selectedStatus.value,
      comment.value || undefined
    )
    
    notifications.notifySuccess(
      t('taskWorkspace.statusTransition.successMessage', { 
        status: t(`taskWorkspace.statusOptions.${selectedStatus.value}`) 
      })
    )
    
    // Reload timeline to show the new status
    await loadStatusTimeline()
    
    emit('success')
    emit('close')
    resetModal()
  } catch (error: any) {
    console.error('Failed to change task status:', error)
    
    // Check for specific error codes from backend
    const errorCode = error.response?.data?.code
    if (errorCode) {
      notifications.notifyError(t(`errors.business.${errorCode}`))
    } else {
      notifications.notifyError(t('taskWorkspace.statusTransition.errorMessage'))
    }
  } finally {
    isLoading.value = false
  }
}

const handleCancel = () => {
  if (!isLoading.value) {
    emit('cancel')
    emit('close')
    resetModal()
  }
}

const resetModal = () => {
  selectedStatus.value = null
  comment.value = ''
  possibleStatuses.value = []
  statusTimeline.value = []
}

// Timeline step icon generation
const getTimelineStepIcon = (status: string, step: TimelineStep): VNode => {
  const iconProps = {
    viewBox: '0 0 24 24',
    width: 20,
    height: 20
  }

  const commonProps = {
    fill: step.isCurrent || step.isCompleted ? 'currentColor' : 'none',
    stroke: 'currentColor',
    'stroke-width': 2
  }

  switch (status) {
    case 'PENDING':
      return h('svg', iconProps, [
        h('circle', { cx: 12, cy: 12, r: 8, ...commonProps })
      ])
    case 'IN_PROGRESS':
      return h('svg', iconProps, [
        h('path', { d: 'M8,5.14V19.14L19,12.14L8,5.14Z', fill: 'currentColor' })
      ])
    case 'ON_HOLD':
      return h('svg', iconProps, [
        h('rect', { x: 6, y: 4, width: 4, height: 16, rx: 1, fill: 'currentColor' }),
        h('rect', { x: 14, y: 4, width: 4, height: 16, rx: 1, fill: 'currentColor' })
      ])
    case 'COMPLETED':
      return h('svg', iconProps, [
        h('polyline', { 
          points: '20 6 9 17 4 12',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 2 
        })
      ])
    case 'CANCELLED':
      return h('svg', iconProps, [
        h('line', { x1: 18, y1: 6, x2: 6, y2: 18, stroke: 'currentColor', 'stroke-width': 2 }),
        h('line', { x1: 6, y1: 6, x2: 18, y2: 18, stroke: 'currentColor', 'stroke-width': 2 })
      ])
    default:
      return h('svg', iconProps, [
        h('circle', { cx: 12, cy: 12, r: 8, ...commonProps })
      ])
  }
}

// Watch for modal visibility changes
watch(() => props.isVisible, (visible: boolean) => {
  if (visible) {
    loadPossibleStatuses()
    loadStatusTimeline()
  } else {
    resetModal()
  }
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AppModal, {
    show: _ctx.isVisible,
    title: _ctx.$t('taskWorkspace.statusTransition.title'),
    size: "large",
    "gradient-header": false,
    loading: isLoading.value,
    onClose: handleCancel
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_30, [
        _createVNode(AppButton, {
          type: "button",
          variant: "outline",
          onClick: handleCancel,
          disabled: isLoading.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
          ]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(AppButton, {
          type: "button",
          variant: "primary",
          onClick: handleConfirm,
          disabled: !selectedStatus.value || isLoading.value,
          loading: isLoading.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.confirm')), 1)
          ]),
          _: 1
        }, 8, ["disabled", "loading"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("h4", _hoisted_4, [
              _cache[1] || (_cache[1] = _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                width: "20",
                height: "20",
                class: "timeline-section-icon"
              }, [
                _createElementVNode("path", {
                  fill: "currentColor",
                  d: "M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12.5,7V13L16,15.5L15,17L11,14V7H12.5Z"
                })
              ], -1)),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('taskWorkspace.statusWorkflow')), 1)
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.$t('taskWorkspace.statusTransition.currentStatus')) + ": ", 1),
              _createElementVNode("div", {
                class: _normalizeClass(["status-badge", `status-badge--${_ctx.currentStatus?.toLowerCase()}`])
              }, _toDisplayString(_ctx.$t(`taskWorkspace.statusOptions.${_ctx.currentStatus}`)), 3)
            ])
          ]),
          (loadingTimeline.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('common.loading')) + "...", 1)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timelineSteps.value, (step, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `step-${index}`,
                    class: _normalizeClass(["timeline-step", {
              current: step.isCurrent,
              completed: step.isCompleted
            }])
                  }, [
                    _createElementVNode("div", _hoisted_9, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(getTimelineStepIcon(step.status, step))))
                    ]),
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.$t(`taskWorkspace.statusOptions.${step.status}`)), 1),
                      (step.date)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(step.date), 1))
                        : _createCommentVNode("", true)
                    ])
                  ], 2))
                }), 128))
              ]))
        ]),
        (possibleStatuses.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createElementVNode("label", _hoisted_14, _toDisplayString(_ctx.$t('taskWorkspace.statusTransition.selectNewStatus')), 1),
              _createElementVNode("div", _hoisted_15, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(possibleStatuses.value, (status) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: status,
                    type: "button",
                    class: _normalizeClass(["status-option", { 'status-option--selected': selectedStatus.value === status }]),
                    onClick: ($event: any) => (selectedStatus.value = status)
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["status-option__icon", `status-option__icon--${status.toLowerCase()}`])
                    }, [
                      (_openBlock(), _createElementBlock("svg", _hoisted_17, [
                        (status === 'PENDING')
                          ? (_openBlock(), _createElementBlock("circle", _hoisted_18))
                          : (status === 'IN_PROGRESS')
                            ? (_openBlock(), _createElementBlock("path", _hoisted_19))
                            : (status === 'ON_HOLD')
                              ? (_openBlock(), _createElementBlock("path", _hoisted_20))
                              : (status === 'COMPLETED')
                                ? (_openBlock(), _createElementBlock("path", _hoisted_21))
                                : (status === 'CANCELLED')
                                  ? (_openBlock(), _createElementBlock("path", _hoisted_22))
                                  : (_openBlock(), _createElementBlock("path", _hoisted_23))
                      ]))
                    ], 2),
                    _createElementVNode("div", _hoisted_24, [
                      _createElementVNode("div", _hoisted_25, _toDisplayString(getActionLabel(status)), 1)
                    ]),
                    (selectedStatus.value === status)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_26, [...(_cache[2] || (_cache[2] = [
                          _createElementVNode("svg", {
                            viewBox: "0 0 24 24",
                            width: "20",
                            height: "20"
                          }, [
                            _createElementVNode("path", {
                              fill: "currentColor",
                              d: "M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"
                            })
                          ], -1)
                        ]))]))
                      : _createCommentVNode("", true)
                  ], 10, _hoisted_16))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true),
        (selectedStatus.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
              _createElementVNode("label", _hoisted_28, _toDisplayString(_ctx.$t('taskWorkspace.statusTransition.comment')), 1),
              _withDirectives(_createElementVNode("textarea", {
                id: "status-comment",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((comment).value = $event)),
                class: "comment-textarea",
                placeholder: _ctx.$t('taskWorkspace.statusTransition.commentPlaceholder'),
                rows: "3"
              }, null, 8, _hoisted_29), [
                [_vModelText, comment.value]
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["show", "title", "loading"]))
}
}

})